import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/client";
import { BECOME_TEACHER } from "../../../apollo-client/mutations";
import Dialog from "@mui/material/Dialog";
import { FormattedMessage } from "react-intl";
import {
  GET_COURSE_TEACHINGS,
  GET_USER_COURSES,
  GET_USER_HEADER,
} from "../../../apollo-client/queries";
import { showErrorToast } from "../../../toast/toast-service";
import { useIsMobile } from "../../../hooks/state-checkers/use-is-mobile";
import { GET_USER_TEACHINGS } from "../../../apollo-client/queries/teaching-queries";
import { TeachingDialogLayout } from "./shared/TeachingDialogLayout";
import { TeachingFormFields } from "./shared/TeachingFormFields";
import { TeachingDialogActions } from "./shared/TeachingDialogActions";

export const BecomeTeacherDialog = ({
  isDialogOpen,
  closeDialog,
  user,
  courseId,
  inactiveTeaching,
}) => {
  const defaultTeachingConfig = {
    price: 0,
    acceptsRemote: true,
    description: "",
    teacher: {
      username: user && user.username,
    },
    ...inactiveTeaching,
  };

  const [formValues, setFormValues] = useState({
    price: defaultTeachingConfig.price,
    acceptsRemote: defaultTeachingConfig.acceptsRemote,
    description: defaultTeachingConfig.description,
  });

  const [formValueErrors, setFormValueErrors] = useState({
    price: false,
    acceptsRemote: false,
    description: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [becomeTeacherMutation] = useMutation(BECOME_TEACHER, {
    variables: {
      course: courseId,
      price: parseInt(formValues.price),
      description: formValues.description,
    },
    refetchQueries: [
      GET_COURSE_TEACHINGS,
      "teachings",
      GET_USER_TEACHINGS,
      GET_USER_HEADER,
      GET_USER_COURSES,
    ],
  });

  const handleBecomeTeacher = async () => {
    setIsSubmitting(true);
    try {
      await becomeTeacherMutation();
      setIsSuccess(true);
      // Show success state briefly before closing
      setTimeout(() => {
        closeDialog();
        setIsSuccess(false);
        setIsSubmitting(false);
      }, 1500);
    } catch (err) {
      setIsSubmitting(false);
      showErrorToast(err.message);
    }
  };

  const handleCloseDialog = () => {
    closeDialog();
  };

  const isMobile = useIsMobile();

  return (
    <Dialog
      open={isDialogOpen}
      onClose={() => handleCloseDialog()}
      fullWidth={true}
      fullScreen={isMobile}
      maxWidth={"md"}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          boxShadow: isMobile ? 'none' : '0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12), 0 11px 15px -7px rgba(0,0,0,0.2)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: isMobile ? '100vh' : '90vh',
        },
      }}
    >
      <TeachingDialogLayout
        isOpen={isDialogOpen}
        isMobile={isMobile}
        onClose={handleCloseDialog}
        title={defaultTeachingConfig.teacher.username}
        subtitle={<FormattedMessage id="teacher.become-teacher" defaultMessage="Become a Teacher" />}
        showTooltip={true}
        tooltipId="teacher.add-new-teacher-hint"
        showSuccess={isSuccess}
        successTitle={<FormattedMessage id="teacher.success.title" defaultMessage="Success!" />}
        successMessage={<FormattedMessage id="teacher.success.message" defaultMessage="You are now a teacher for this course!" />}
        actions={
          <TeachingDialogActions
            onCancel={handleCloseDialog}
            onConfirm={handleBecomeTeacher}
            confirmText={<FormattedMessage id="teacher.add-new-teacher" />}
            isSubmitting={isSubmitting}
            isDisabled={!!formValueErrors.price || !!formValueErrors.description}
          />
        }
      >
        <TeachingFormFields
          formValues={formValues}
          formValueErrors={formValueErrors}
          onFormValuesChange={setFormValues}
          onFormValueErrorsChange={setFormValueErrors}
          isSubmitting={isSubmitting}
          showDescriptionValidation={true}
        />
      </TeachingDialogLayout>
    </Dialog>
  );
};

BecomeTeacherDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  closeDialog: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  courseId: PropTypes.string.isRequired,
};
